// Configuración contable: mapeo concepto → cuenta para la contabilización
// automática, en pestañas por módulo (el campo `modulo` viene de cont_config_get:
// un módulo nuevo solo agrega filas en la BD y su pestaña aparece sola).
// Contabilidad → Catálogos → Configuración Contable.
import { useMemo, useState } from 'react';
import { AvisoRenombre } from '@/components/ui/AvisoRenombre';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { BuscadorCuenta } from '@/components/ui/BuscadorCuenta';
import { rpc } from './shared';

interface ConfigRow { concepto: string; label: string; modulo: string; cuenta_id: string | null; codigo: string | null; nombre: string | null }

const DESCRIPCION: Record<string, string> = {
  'Facturación y Compras': 'Al autorizar facturas/NC/retenciones o procesar compras, el sistema genera el asiento usando estas cuentas (arrastrando el proyecto a las líneas).',
  'Nómina': 'Usadas por los botones «Contabilizar» del rol de pagos, los décimos, las utilidades y los finiquitos (Nómina). Los décimos liquidan contra la cuenta de provisiones.',
};

export function ConfigContable() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: config = [], isFetching } = useQuery<ConfigRow[]>({ queryKey: ['cont-config'], queryFn: () => rpc<ConfigRow[]>('cont_config_get') });

  const modulos = useMemo(() => {
    const orden: string[] = [];
    for (const r of config) if (!orden.includes(r.modulo)) orden.push(r.modulo);
    return orden;
  }, [config]);
  const [tab, setTab] = useState<string | null>(null);
  const activo = tab ?? modulos[0] ?? null;
  const filas = useMemo(() => config.filter((r) => r.modulo === activo), [config, activo]);
  const sinAsignar = (m: string) => config.filter((r) => r.modulo === m && !r.cuenta_id).length;

  const set = useMutation({
    mutationFn: ({ concepto, cuenta_id }: { concepto: string; cuenta_id: string }) => rpc('cont_config_set', { _concepto: concepto, _cuenta_id: cuenta_id || null }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['cont-config'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const backfill = useMutation({
    mutationFn: () => rpc<{ contabilizados: number }>('cont_contabilizar_pendientes'),
    onSuccess: (r) => toast.success(`Documentos contabilizados: ${r.contabilizados}`),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <AvisoRenombre clave="cont-config" antes="Configuración Contable" />
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Parámetros contables</h1>
        <Button variant="outline" isLoading={backfill.isPending} onClick={async () => { if (await dlg.confirmar('¿Contabilizar todos los documentos pendientes?', { detalle: 'Se contabilizarán los documentos autorizados/procesados que aún no tienen asiento.' })) backfill.mutate(); }}>
          Contabilizar pendientes
        </Button>
      </div>

      <div className="flex gap-1 border-b">
        {modulos.map((m) => (
          <button key={m} aria-pressed={activo === m}
            className={`px-4 py-2 text-sm rounded-t-md inline-flex items-center gap-2 ${activo === m ? 'bg-card border border-b-0 font-medium' : 'text-muted-foreground hover:bg-muted'}`}
            onClick={() => setTab(m)}>
            {m}
            {sinAsignar(m) > 0 && (
              <span className="px-1.5 py-0.5 rounded-full text-xs bg-pendiente-soft text-pendiente">{sinAsignar(m)}</span>
            )}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Mapeo de cuentas — {activo ?? ''}
            {activo && sinAsignar(activo) > 0 && (
              <span className="ml-2 text-xs font-normal text-pendiente">{sinAsignar(activo)} sin asignar</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {activo && DESCRIPCION[activo] && <p className="text-sm text-muted-foreground">{DESCRIPCION[activo]}</p>}
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filas.map((r) => (
              <label key={r.concepto} className="space-y-1">
                <span className={`text-xs font-medium ${r.cuenta_id ? 'text-muted-foreground' : 'text-pendiente'}`}>{r.label}</span>
                <BuscadorCuenta value={r.cuenta_id ?? ''} onChange={(v) => set.mutate({ concepto: r.concepto, cuenta_id: v })} allowClear placeholder="— Sin asignar —" />
              </label>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
